<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { CONTROL_HEIGHT, setsDimension } from './controlStyles'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface Props {
  variant?: ButtonVariant
  size?: ButtonSize
  type?: 'button' | 'submit' | 'reset'
  loading?: boolean
  disabled?: boolean
  /** Full width — the pattern used by form footers and mobile actions. */
  block?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  type: 'button',
})

/**
 * Teal is deliberately absent here. accent-500 behind white text is 1.98:1, so
 * it is never an action fill; navy carries the actions and teal marks state.
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 border-transparent',
  secondary: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 active:bg-gray-100',
  ghost: 'bg-transparent text-gray-700 border-transparent hover:bg-gray-100 active:bg-gray-200',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 border-transparent',
}

/**
 * Horizontal padding only. Height is fixed from the same tokens the form
 * controls use, so a button lines up with a select or input beside it in a
 * toolbar. Deriving height from padding produced 32px, 34px and 38px on one
 * row — the button had a border, the next one did not, and that was the
 * difference.
 */
const SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 text-sm gap-1.5',
  md: 'px-4 text-sm gap-2',
  lg: 'px-6 text-base gap-2',
}

const isDisabled = computed(() => props.disabled || props.loading)

/** An icon button may set its own square size; yield to it. */
const attrs = useAttrs()
const ownHeight = computed(() => setsDimension(attrs.class, 'h-'))
</script>

<template>
  <button
    :type="type"
    :disabled="isDisabled"
    :aria-busy="loading || undefined"
    class="inline-flex items-center justify-center rounded-md border font-medium transition-colors
           focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-2
           disabled:opacity-50 disabled:cursor-not-allowed"
    :class="[
      VARIANT[variant],
      SIZE[size],
      ownHeight ? '' : CONTROL_HEIGHT[size],
      block ? 'w-full' : '',
    ]"
  >
    <svg v-if="loading" class="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
    <slot />
  </button>
</template>
